
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { History, Zap, Sparkles, Loader2, Target, Tag, Link2, Trash2, MoreVertical, Heart, Brain, Users, Sparkle, Wallet, TrendingUp, TrendingDown, Search, X, Filter, MapPin, LayoutGrid, ChevronDown, ChevronUp, Compass, RefreshCw, Check } from 'lucide-react';
import { JournalEntry, LifeContext, LinkType, DimensionImpacts, AlignmentFilter, AIPersonalityKey } from '../types';
import { UI_STRINGS } from '../constants/translations';
import { trackPageView } from '../src/lib/analytics';
import { useFeatureDiscovery } from '../contexts/FeatureDiscoveryContext';
import FeatureDiscovery from '../components/FeatureDiscovery';
import ConsciousnessFeed from '../components/ConsciousnessFeed';
import InstagramFeed from '../components/InstagramFeed';
import { useMomentAdvice } from '../hooks/useMomentAdvice';
import { useDailyGuide } from '../hooks/useDailyGuide';
import { PersonalityHeaderButton, PersonalitySelectorModal } from '../components/PersonalitySelector';
import { DEFAULT_PERSONALITY_KEY } from '../shared/aiPersonalities';

const DIMENSION_CONFIG = {
  salute: { icon: Heart, key: 'Health' as const, bgPos: 'bg-rose-50', textPos: 'text-rose-600', borderPos: 'border-rose-100' },
  focus: { icon: Brain, key: 'Focus' as const, bgPos: 'bg-amber-50', textPos: 'text-amber-600', borderPos: 'border-amber-100' },
  connessione: { icon: Users, key: 'Connection' as const, bgPos: 'bg-blue-50', textPos: 'text-blue-600', borderPos: 'border-blue-100' },
  spirito: { icon: Sparkle, key: 'Spirit' as const, bgPos: 'bg-purple-50', textPos: 'text-purple-600', borderPos: 'border-purple-100' },
  finanza: { icon: Wallet, key: 'Finance' as const, bgPos: 'bg-emerald-50', textPos: 'text-emerald-600', borderPos: 'border-emerald-100' },
};

interface JournalViewProps {
  context: LifeContext;
  entries: JournalEntry[];
  entriesLoading?: boolean;
  onDeleteEntry?: (id: string) => void;
  onLikeEntry?: (id: string, isLiked: boolean) => void;
  alignmentFilter?: AlignmentFilter;
  onClearFilter?: () => void;
  onNavigate?: (screen: 'pulse' | 'archive' | 'consult' | 'decisions' | 'insights') => void;
  onPersonalityChange?: (key: AIPersonalityKey) => Promise<void>;
  useInstagramLayout?: boolean;
}

const getLinkTypeLabels = (language: string): Record<LinkType, string> => {
  const labels: Record<string, Record<LinkType, string>> = {
    Italian: {
      similar: 'Momento simile',
      cause: 'Ha causato questo',
      effect: 'Conseguenza di questo',
      contradiction: 'In contrasto',
      progression: 'Evoluzione',
      related_decision: 'Decisione collegata',
      relatedPerson: 'Persona collegata',
      patternEvidence: 'Pattern ricorrente'
    },
    English: {
      similar: 'Similar moment',
      cause: 'Caused this',
      effect: 'Result of this',
      contradiction: 'In contrast',
      progression: 'Evolution',
      related_decision: 'Related decision',
      relatedPerson: 'Connected person',
      patternEvidence: 'Recurring pattern'
    },
    Spanish: {
      similar: 'Momento similar',
      cause: 'Causó esto',
      effect: 'Consecuencia de esto',
      contradiction: 'En contraste',
      progression: 'Evolución',
      related_decision: 'Decisión relacionada',
      relatedPerson: 'Persona conectada',
      patternEvidence: 'Patrón recurrente'
    },
    German: {
      similar: 'Ähnlicher Moment',
      cause: 'Hat dies verursacht',
      effect: 'Folge davon',
      contradiction: 'Im Gegensatz',
      progression: 'Entwicklung',
      related_decision: 'Verbundene Entscheidung',
      relatedPerson: 'Verbundene Person',
      patternEvidence: 'Wiederkehrendes Muster'
    },
    French: {
      similar: 'Moment similaire',
      cause: 'A causé cela',
      effect: 'Conséquence de cela',
      contradiction: 'En contraste',
      progression: 'Évolution',
      related_decision: 'Décision liée',
      relatedPerson: 'Personne connectée',
      patternEvidence: 'Schéma récurrent'
    }
  };
  return labels[language] || labels.English;
};

const JournalView: React.FC<JournalViewProps> = ({ context, entries, entriesLoading = false, onDeleteEntry, onLikeEntry, alignmentFilter, onClearFilter, onNavigate, onPersonalityChange, useInstagramLayout = true }) => {
  const t = useMemo(() => UI_STRINGS[context.language] || UI_STRINGS.English, [context.language]);
  const linkTypeLabels = useMemo(() => getLinkTypeLabels(context.language), [context.language]);
  const safeEntries = Array.isArray(entries) ? entries : [];
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'feed' | 'classic'>('feed');
  const [expandedConnectionsId, setExpandedConnectionsId] = useState<string | null>(null);
  const [expandedAdviceId, setExpandedAdviceId] = useState<string | null>(null);
  const { state: discoveryState, getNextStepForView, completeStep } = useFeatureDiscovery();
  const currentDiscoveryStep = useMemo(() => getNextStepForView('journal'), [getNextStepForView]);

  // AI Personality state
  const [isPersonalityModalOpen, setIsPersonalityModalOpen] = useState(false);
  const currentPersonalityKey = context.selectedPersonalityKey || DEFAULT_PERSONALITY_KEY;
  
  const {
    adviceMap,
    weeklyRecaps,
    generatingAdviceForIds,
    generateAdvice,
    completeAction,
    markRecapViewed,
  } = useMomentAdvice();
  
  const {
    dailyGuide,
    isLoading: isDailyGuideLoading,
    isGenerating: isGeneratingDailyGuide,
    isExpired: isDailyGuideExpired,
    generateDailyGuide,
    completeAction: completeDailyGuideAction,
  } = useDailyGuide();
  
  const [expandedDailyGuide, setExpandedDailyGuide] = useState(false);

  const handleDiscoveryActivate = useCallback((step: keyof typeof discoveryState) => {
    if (step === 'archiveIntro' || step === 'journalDiscovered') {
      completeStep(step);
    }
  }, [completeStep]);

  useEffect(() => {
    trackPageView('/journal', 'Journal', true);
  }, []);
  
  const alignmentFilterLabels: Record<string, Record<string, string>> = {
    aligned: { Italian: 'Allineati', Spanish: 'Alineados', English: 'Aligned', German: 'Ausgerichtet', French: 'Alignés' },
    neutral: { Italian: 'Neutri', Spanish: 'Neutrales', English: 'Neutral', German: 'Neutral', French: 'Neutres' },
    misaligned: { Italian: 'Disallineati', Spanish: 'Desalineados', English: 'Misaligned', German: 'Fehlausgerichtet', French: 'Désalignés' }
  };
  
  // Sort entries from newest to oldest
  const sortedEntries = useMemo(() => {
    return [...safeEntries].sort((a, b) => b.timestamp - a.timestamp);
  }, [safeEntries]);
  
  // Filter entries based on search, category, and alignment
  const filteredEntries = useMemo(() => {
    return sortedEntries.filter(entry => {
      const matchesSearch = !searchQuery || 
        entry.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.category?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = !filterCategory || entry.category === filterCategory;
      
      const ga = typeof entry.goalAlignment === 'number' ? entry.goalAlignment : 50;
      const matchesAlignment = !alignmentFilter || 
        (alignmentFilter === 'aligned' && ga >= 60) ||
        (alignmentFilter === 'neutral' && ga > 40 && ga < 60) ||
        (alignmentFilter === 'misaligned' && ga <= 40);
      
      return matchesSearch && matchesCategory && matchesAlignment;
    });
  }, [sortedEntries, searchQuery, filterCategory, alignmentFilter]);
  
  // Get unique categories for filter
  const categories = useMemo(() => {
    const cats = new Set<string>();
    safeEntries.forEach(e => e.category && cats.add(e.category));
    return Array.from(cats);
  }, [safeEntries]);
  
  // Create a map for fast entry lookup by ID (handles entries not in current view)
  const entriesMap = useMemo(() => {
    const map = new Map<string, JournalEntry>();
    safeEntries.forEach(e => map.set(e.id, e));
    return map;
  }, [safeEntries]);
  
  /* CONSCIOUSNESS FEED VIEW - COMMENTED OUT FOR NOW
  if (viewMode === 'feed') {
    return (
      <div className="h-full flex flex-col bg-[#F5F5F7] view-enter overflow-hidden relative">
        <ConsciousnessFeed
          context={context}
          entries={safeEntries}
          adviceMap={adviceMap}
          weeklyRecaps={weeklyRecaps}
          onDeleteEntry={onDeleteEntry}
          onGenerateAdvice={generateAdvice}
          onCompleteAction={completeAction}
          onViewRecap={markRecapViewed}
          generatingAdviceForIds={generatingAdviceForIds}
        />
        
        <button
          onClick={() => setViewMode('classic')}
          className="fixed bottom-24 right-4 z-20 w-12 h-12 rounded-2xl bg-white shadow-lg border border-black/[0.06] flex items-center justify-center text-black/40 hover:text-black/60 transition-colors"
          title="Classic View"
        >
          <LayoutGrid size={20} />
        </button>

        {currentDiscoveryStep && (
          <FeatureDiscovery
            featureKey={currentDiscoveryStep}
            discoveryState={discoveryState}
            onDismiss={completeStep}
            onActivate={handleDiscoveryActivate}
            onNavigate={onNavigate}
            currentView="archive"
            language={context.language}
            position="bottom"
          />
        )}
      </div>
    );
  }
  */
  
  // Multilingual analyzing state messages
  const analyzingTexts: Record<string, { moment: string; photo: string; note: string }> = {
    Italian: { moment: 'Sto analizzando il momento...', photo: 'Sto analizzando la foto...', note: 'Sto analizzando la nota...' },
    English: { moment: 'Analyzing moment...', photo: 'Analyzing photo...', note: 'Analyzing note...' },
    Spanish: { moment: 'Analizando momento...', photo: 'Analizando foto...', note: 'Analizando nota...' },
    German: { moment: 'Moment wird analysiert...', photo: 'Foto wird analysiert...', note: 'Notiz wird analysiert...' },
    French: { moment: 'Analyse du moment...', photo: 'Analyse de la photo...', note: 'Analyse de la note...' },
  };
  const analyzingT = analyzingTexts[context.language] || analyzingTexts.English;

  const guideTranslations = {
    Italian: {
      title: 'La Tua Guida',
      subtitle: 'Basata su tutti i tuoi momenti',
      discoverDirection: 'Genera la tua guida',
      discoverSubtitle: 'Ricevi una guida personalizzata basata su tutti i tuoi momenti',
      generating: 'Riflettendo sui tuoi momenti...',
      viewFullPlan: 'Vedi piano completo',
      closePlan: 'Chiudi piano',
      shortTerm: 'Oggi',
      mediumTerm: 'Questa Settimana',
      longTerm: 'Visione',
      completed: 'Completato',
      refresh: 'Aggiorna',
      basedOn: 'Basato su',
      moments: 'momenti recenti',
      morning: 'Guida Mattutina',
      evening: 'Guida Serale',
    },
    English: {
      title: 'Your Guide',
      subtitle: 'Based on all your moments',
      discoverDirection: 'Generate your guide',
      discoverSubtitle: 'Get personalized guidance based on all your moments',
      generating: 'Reflecting on your moments...',
      viewFullPlan: 'View full plan',
      closePlan: 'Close plan',
      shortTerm: 'Today',
      mediumTerm: 'This Week',
      longTerm: 'Vision',
      completed: 'Completed',
      refresh: 'Refresh',
      basedOn: 'Based on',
      moments: 'recent moments',
      morning: 'Morning Guide',
      evening: 'Evening Guide',
    },
    Spanish: {
      title: 'Tu Guía',
      subtitle: 'Basada en todos tus momentos',
      discoverDirection: 'Genera tu guía',
      discoverSubtitle: 'Recibe orientación personalizada basada en todos tus momentos',
      generating: 'Reflexionando sobre tus momentos...',
      viewFullPlan: 'Ver plan completo',
      closePlan: 'Cerrar plan',
      shortTerm: 'Hoy',
      mediumTerm: 'Esta Semana',
      longTerm: 'Visión',
      completed: 'Completado',
      refresh: 'Actualizar',
      basedOn: 'Basado en',
      moments: 'momentos recientes',
      morning: 'Guía Matutina',
      evening: 'Guía Vespertina',
    },
    German: {
      title: 'Dein Wegweiser',
      subtitle: 'Basierend auf all deinen Momenten',
      discoverDirection: 'Generiere deinen Wegweiser',
      discoverSubtitle: 'Erhalte personalisierte Anleitung basierend auf allen deinen Momenten',
      generating: 'Reflektiere über deine Momente...',
      viewFullPlan: 'Vollständigen Plan anzeigen',
      closePlan: 'Plan schließen',
      shortTerm: 'Heute',
      mediumTerm: 'Diese Woche',
      longTerm: 'Vision',
      completed: 'Abgeschlossen',
      refresh: 'Aktualisieren',
      basedOn: 'Basierend auf',
      moments: 'aktuellen Momenten',
      morning: 'Morgen-Wegweiser',
      evening: 'Abend-Wegweiser',
    },
    French: {
      title: 'Ton Guide',
      subtitle: 'Basé sur tous tes moments',
      discoverDirection: 'Génère ton guide',
      discoverSubtitle: 'Reçois des conseils personnalisés basés sur tous tes moments',
      generating: 'Réflexion sur tes moments...',
      viewFullPlan: 'Voir le plan complet',
      closePlan: 'Fermer le plan',
      shortTerm: "Aujourd'hui",
      mediumTerm: 'Cette Semaine',
      longTerm: 'Vision',
      completed: 'Terminé',
      refresh: 'Actualiser',
      basedOn: 'Basé sur',
      moments: 'moments récents',
      morning: 'Guide Matinal',
      evening: 'Guide du Soir',
    },
  };
  const guideT = guideTranslations[context.language] || guideTranslations.English;

  // Use Instagram-style layout (default)
  if (useInstagramLayout) {
    return (
      <div className="h-full flex flex-col bg-white view-enter overflow-hidden">
        <InstagramFeed
          context={context}
          entries={entries}
          entriesLoading={entriesLoading}
          onDeleteEntry={onDeleteEntry}
          onLikeEntry={onLikeEntry}
          onPersonalityChange={onPersonalityChange}
        />
        {currentDiscoveryStep && (
          <FeatureDiscovery
            featureKey={currentDiscoveryStep}
            discoveryState={discoveryState}
            onDismiss={completeStep}
            onActivate={handleDiscoveryActivate}
            onNavigate={onNavigate}
            currentView="archive"
            language={context.language}
            position="bottom"
          />
        )}
      </div>
    );
  }

  // Classic layout (fallback)
  return (
    <div className="h-full flex flex-col bg-[#F5F5F7] view-enter overflow-hidden">
      <header className="px-6 lg:px-12 xl:px-16 2xl:px-20 pt-5 lg:pt-5 pb-3 lg:pb-4 shrink-0 bg-white/95 backdrop-blur-xl border-b border-black/[0.03] sticky top-0 z-10">
        <div className="max-w-6xl 2xl:max-w-7xl mx-auto space-y-3 lg:space-y-4">
          {/* Title Row */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 lg:gap-6">
            <div className="flex items-center justify-between w-full lg:w-auto">
              <div>
                <h1 className="text-[30px] lg:text-4xl xl:text-[42px] font-black tracking-tighter leading-none">{t.journal.title}</h1>
                <div className="flex items-center gap-2 mt-1.5 lg:mt-2">
                  <span className="text-[11px] lg:text-sm font-black uppercase tracking-widest text-black/20">
                    {filteredEntries.length} {t.journal.entries}
                    {(searchQuery || filterCategory || alignmentFilter) && ` · ${safeEntries.length} ${t.journal.allCategories || 'total'}`}
                  </span>
                  {alignmentFilter && (
                    <button
                      onClick={onClearFilter}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold transition-colors ${
                        alignmentFilter === 'aligned' ? 'bg-emerald-100 text-emerald-700' :
                        alignmentFilter === 'neutral' ? 'bg-amber-100 text-amber-700' :
                        'bg-rose-100 text-rose-700'
                      }`}
                    >
                      {alignmentFilterLabels[alignmentFilter]?.[context.language] || alignmentFilter}
                      <X size={10} />
                    </button>
                  )}
                </div>
              </div>
              {/* AI Personality Button - Right side on mobile */}
              <div className="lg:hidden">
                <PersonalityHeaderButton
                  personalityKey={currentPersonalityKey}
                  language={context.language}
                  onClick={() => setIsPersonalityModalOpen(true)}
                />
              </div>
            </div>
            
            {/* Apple-style Search Bar + Desktop Personality Button */}
            <div className="flex items-center gap-3">
              <div className="relative w-full lg:w-auto">
                <div className="flex items-center gap-2 px-4 lg:px-5 py-2.5 lg:py-3 bg-black/[0.04] hover:bg-black/[0.06] rounded-2xl lg:rounded-[20px] transition-all lg:w-80 xl:w-96 group focus-within:ring-2 focus-within:ring-indigo-300 focus-within:bg-white focus-within:shadow-lg">
                  <Search size={18} className="text-black/30 shrink-0" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t.journal.searchPlaceholder || "Search moments..."}
                    className="flex-1 bg-transparent text-[15px] lg:text-[16px] font-medium outline-none placeholder:text-black/30"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="w-5 h-5 rounded-full bg-black/10 hover:bg-black/20 flex items-center justify-center transition-colors"
                    >
                      <X size={12} className="text-black/50" />
                    </button>
                  )}
                </div>
              </div>
              {/* AI Personality Button - Desktop only (mobile is in title row) */}
              <div className="hidden lg:block">
                <PersonalityHeaderButton
                  personalityKey={currentPersonalityKey}
                  language={context.language}
                  onClick={() => setIsPersonalityModalOpen(true)}
                />
              </div>
            </div>
          </div>
          
          {/* Category Filter Chips */}
          {categories.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
              <button
                onClick={() => setFilterCategory(null)}
                className={`shrink-0 px-4 lg:px-5 py-2 lg:py-2.5 rounded-full text-[13px] lg:text-[14px] font-bold transition-all duration-300 ${
                  !filterCategory 
                    ? 'bg-[#1D1D1F] text-white shadow-lg shadow-black/20' 
                    : 'bg-black/[0.04] text-black/50 hover:bg-black/[0.08] hover:text-black/70'
                }`}
              >
                {t.journal.allCategories || "All"}
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setFilterCategory(filterCategory === cat ? null : cat)}
                  className={`shrink-0 px-4 lg:px-5 py-2 lg:py-2.5 rounded-full text-[13px] lg:text-[14px] font-bold transition-all duration-300 ${
                    filterCategory === cat 
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-300/40' 
                      : 'bg-black/[0.04] text-black/50 hover:bg-black/[0.08] hover:text-black/70'
                  }`}
                >
                  {cat}
                </button>
              ))}
              {filterCategory && (
                <button
                  onClick={() => setFilterCategory(null)}
                  className="shrink-0 ml-2 flex items-center gap-1.5 px-3 py-2 text-[12px] font-bold text-rose-500 hover:text-rose-600 transition-colors"
                >
                  <X size={14} />
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 lg:px-12 xl:px-20 2xl:px-32 py-6 lg:py-10 no-scrollbar pb-32 lg:pb-44">
        <div className="max-w-6xl 2xl:max-w-7xl mx-auto">
        
        {/* Daily Guide - Context-Based Personalized Guidance */}
        <div className="mb-5 lg:mb-6">
          {!dailyGuide || isDailyGuideExpired() ? (
            <button
              onClick={() => generateDailyGuide()}
              disabled={isGeneratingDailyGuide}
              className="w-full p-5 lg:p-6 bg-white hover:bg-gray-50/80 rounded-2xl lg:rounded-3xl border border-black/[0.04] shadow-sm hover:shadow-md transition-all flex items-center gap-4"
            >
              <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg shrink-0">
                {isGeneratingDailyGuide ? (
                  <Loader2 size={18} className="text-white animate-spin" />
                ) : (
                  <Sparkles size={18} className="text-white" />
                )}
              </div>
              <div className="flex-1 text-left">
                <p className="text-[15px] lg:text-[16px] font-bold text-[#1D1D1F]">
                  {isGeneratingDailyGuide ? guideT.generating : guideT.discoverDirection}
                </p>
                <p className="text-[12px] lg:text-[13px] text-black/50 mt-0.5">
                  {guideT.discoverSubtitle}
                </p>
              </div>
            </button>
          ) : (
            <div className="bg-white rounded-2xl lg:rounded-3xl border border-black/[0.04] shadow-sm overflow-hidden">
              <div className="p-5 lg:p-6">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-[10px] lg:text-[11px] font-black text-purple-600 uppercase tracking-widest">
                        {guideT.title}
                      </p>
                      <span className="text-[9px] lg:text-[10px] text-black/30 font-medium px-2 py-0.5 bg-black/[0.03] rounded-full">
                        {dailyGuide.guideType === 'morning' ? guideT.morning : guideT.evening}
                      </span>
                    </div>
                    <p className="text-[15px] lg:text-[17px] font-medium text-[#1D1D1F] leading-relaxed lg:leading-[1.6]">
                      {dailyGuide.dailyInsight}
                    </p>
                    {/* Commented out - no longer showing moment count
                    <p className="text-[10px] lg:text-[11px] text-black/35 mt-3 font-medium">
                      {guideT.basedOn} {dailyGuide.recentMomentsCount} {guideT.moments}
                    </p>
                    */}
                  </div>
                  <button
                    onClick={() => generateDailyGuide(true)}
                    disabled={isGeneratingDailyGuide}
                    className="p-2.5 rounded-xl hover:bg-purple-50 transition-colors shrink-0"
                    title={guideT.refresh}
                  >
                    <RefreshCw size={16} className={`text-purple-400 ${isGeneratingDailyGuide ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
              
              <button
                onClick={() => setExpandedDailyGuide(!expandedDailyGuide)}
                className="w-full px-5 py-3.5 flex items-center justify-center gap-2 bg-gradient-to-b from-purple-50/50 to-purple-50/80 hover:from-purple-50/80 hover:to-purple-100/80 transition-colors border-t border-purple-100/30"
              >
                <span className="text-[12px] lg:text-[13px] font-bold text-purple-700">
                  {expandedDailyGuide ? guideT.closePlan : guideT.viewFullPlan}
                </span>
                {expandedDailyGuide ? <ChevronUp size={14} className="text-purple-500" /> : <ChevronDown size={14} className="text-purple-500" />}
              </button>
              
              {expandedDailyGuide && (
                <div className="p-4 lg:p-5 pt-2 space-y-3">
                  {dailyGuide.shortTermAction && (
                    <div className="flex items-start gap-3 p-4 bg-amber-50/80 rounded-xl border border-amber-100/80">
                      <button
                        onClick={() => completeDailyGuideAction('short', !dailyGuide.shortTermCompleted)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all mt-0.5 ${
                          dailyGuide.shortTermCompleted 
                            ? 'bg-amber-500 border-amber-500' 
                            : 'border-amber-300 hover:border-amber-400'
                        }`}
                      >
                        {dailyGuide.shortTermCompleted && <Check size={14} className="text-white" />}
                      </button>
                      <div className="flex-1">
                        <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-1.5">
                          💡 {guideT.shortTerm}
                        </p>
                        <p className={`text-[13px] lg:text-[14px] text-amber-900 leading-relaxed ${dailyGuide.shortTermCompleted ? 'line-through opacity-60' : ''}`}>
                          {dailyGuide.shortTermAction}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {dailyGuide.mediumTermGoal && (
                    <div className="flex items-start gap-3 p-4 bg-blue-50/80 rounded-xl border border-blue-100/80">
                      <button
                        onClick={() => completeDailyGuideAction('medium', !dailyGuide.mediumTermCompleted)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all mt-0.5 ${
                          dailyGuide.mediumTermCompleted 
                            ? 'bg-blue-500 border-blue-500' 
                            : 'border-blue-300 hover:border-blue-400'
                        }`}
                      >
                        {dailyGuide.mediumTermCompleted && <Check size={14} className="text-white" />}
                      </button>
                      <div className="flex-1">
                        <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-1.5">
                          🎯 {guideT.mediumTerm}
                        </p>
                        <p className={`text-[13px] lg:text-[14px] text-blue-900 leading-relaxed ${dailyGuide.mediumTermCompleted ? 'line-through opacity-60' : ''}`}>
                          {dailyGuide.mediumTermGoal}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {dailyGuide.longTermVision && (
                    <div className="flex items-start gap-3 p-4 bg-violet-50/80 rounded-xl border border-violet-100/80">
                      <button
                        onClick={() => completeDailyGuideAction('long', !dailyGuide.longTermCompleted)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all mt-0.5 ${
                          dailyGuide.longTermCompleted 
                            ? 'bg-violet-500 border-violet-500' 
                            : 'border-violet-300 hover:border-violet-400'
                        }`}
                      >
                        {dailyGuide.longTermCompleted && <Check size={14} className="text-white" />}
                      </button>
                      <div className="flex-1">
                        <p className="text-[10px] font-black text-violet-600 uppercase tracking-widest mb-1.5">
                          🚀 {guideT.longTerm}
                        </p>
                        <p className={`text-[13px] lg:text-[14px] text-violet-900 leading-relaxed ${dailyGuide.longTermCompleted ? 'line-through opacity-60' : ''}`}>
                          {dailyGuide.longTermVision}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {filteredEntries.length === 0 ? (
          <div className="text-center py-20">
            {entriesLoading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 size={28} className="text-indigo-500 animate-spin" />
                <p className="text-black/40 text-base font-medium">{t.loadingMoments || "Loading moments..."}</p>
              </div>
            ) : (
              <p className="text-black/30 text-lg font-medium">{searchQuery ? (t.journal.noResults || "No results found") : (t.journal.empty || "No moments yet")}</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
          {filteredEntries.map((entry, i) => (
          <div key={entry.id} className={`relative bg-white rounded-[28px] lg:rounded-[32px] p-5 lg:p-6 shadow-sm border spring-up hover:shadow-lg transition-all overflow-hidden ${
            entry.isAnalyzing
              ? 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200'
              : 'border-black/[0.02] hover:border-indigo-100'
          }`} style={{ animationDelay: `${i * 50}ms` }}>

            {/* Analyzing Overlay */}
            {entry.isAnalyzing && (
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/10 z-20 backdrop-blur-[1px] flex items-center justify-center">
                <div className="flex flex-col items-center gap-3 px-6 py-4 bg-white/95 rounded-2xl shadow-xl border border-indigo-100">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                    <div className="spinner spinner-white" />
                  </div>
                  <p className="text-[14px] font-bold text-indigo-700 text-center">
                    {entry.imageUrl ? analyzingT.photo : analyzingT.moment}
                  </p>
                </div>
              </div>
            )}

            {/* Header Row: Emoji + Date/Category + Compact Advice Button + Menu */}
            <div className={`flex justify-between items-start mb-3 ${entry.isAnalyzing ? 'opacity-30' : ''}`}>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-gray-50 flex items-center justify-center text-lg">
                  {entry.emotion || '✨'}
                </div>
                <div>
                  <p className="text-[9px] font-black text-black/20 uppercase tracking-widest">
                    {new Date(entry.timestamp).toLocaleDateString(
                      context.language === 'Italian' ? 'it-IT' :
                      context.language === 'Spanish' ? 'es-ES' :
                      context.language === 'German' ? 'de-DE' :
                      context.language === 'French' ? 'fr-FR' : 'en-US',
                      { day: 'numeric', month: 'short' }
                    )}
                  </p>
                  <span className="text-[10px] font-black text-indigo-500 uppercase">{entry.category || 'Generale'}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="flex items-center gap-0.5 px-1.5 py-1 bg-black/[0.03] rounded-full">
                   <Target size={10} className="text-black/30" />
                   <span className="text-[10px] font-bold text-black/50">{entry.goalAlignment}%</span>
                </div>
                <div className="relative">
                  <button 
                    onClick={() => setOpenMenuId(openMenuId === entry.id ? null : entry.id)}
                    className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <MoreVertical size={14} className="text-black/30" />
                  </button>
                  {openMenuId === entry.id && (
                    <div className="absolute right-0 top-7 bg-white rounded-xl shadow-xl border border-black/5 z-10 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                      <button
                        onClick={() => {
                          if (confirm(t.journal.deleteConfirm)) {
                            onDeleteEntry?.(entry.id);
                          }
                          setOpenMenuId(null);
                        }}
                        className="flex items-center gap-2 px-3 py-2.5 text-rose-500 hover:bg-rose-50 w-full text-left whitespace-nowrap"
                      >
                        <Trash2 size={12} />
                        <span className="text-[11px] font-bold">{t.journal.delete}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {entry.imageUrl && (
              <div className={`mb-3 rounded-xl overflow-hidden aspect-video bg-gray-100 ${entry.isAnalyzing ? 'opacity-30' : ''}`}>
                <img src={entry.imageUrl} className="w-full h-full object-cover" alt="Moment" />
              </div>
            )}

            <h3 className={`text-[16px] font-semibold text-black mb-2 leading-snug ${entry.isAnalyzing ? 'opacity-30' : ''}`}>
              {entry.content}
            </h3>

            {!entry.isAnalyzing && entry.aiInsight && (
              <div className="p-4 bg-indigo-50 rounded-2xl flex items-start gap-3 border border-indigo-100">
                <Zap size={14} className="text-indigo-600 mt-1 shrink-0" />
                <p className="text-[13px] font-bold text-indigo-900 leading-tight">
                  {entry.aiInsight}
                </p>
              </div>
            )}

            {entry.dimensionImpacts && !entry.isAnalyzing && (
              <div className="mt-4 pt-4 border-t border-black/[0.05]">
                <div className="flex items-center gap-1.5 mb-3">
                  <Target size={12} className="text-black/40" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-black/30">{t.journal.vitalImpact}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(entry.dimensionImpacts).map(([key, val]) => {
                    const value = val as number;
                    if (value === 0) return null;
                    const config = DIMENSION_CONFIG[key as keyof typeof DIMENSION_CONFIG];
                    if (!config) return null;
                    const Icon = config.icon;
                    const isPositive = value > 0;
                    return (
                      <div 
                        key={key} 
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold border ${
                          isPositive 
                            ? `${config.bgPos} ${config.textPos} ${config.borderPos}`
                            : 'bg-rose-50 text-rose-600 border-rose-100'
                        }`}
                      >
                        <Icon size={12} />
                        <span>{t.categories[config.key]}</span>
                        <span className={`flex items-center gap-0.5 ${isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {isPositive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                          {isPositive ? '+' : ''}{value}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {entry.tags && entry.tags.length > 0 && !entry.isAnalyzing && (
              <div className="mt-4 flex flex-wrap gap-2">
                {entry.tags.map(tag => (
                  <div key={tag} className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-full border border-black/[0.03]">
                    <Tag size={10} className="text-black/30" />
                    <span className="text-[9px] font-black uppercase text-black/40">{tag}</span>
                  </div>
                ))}
              </div>
            )}

            {/* People & Places - Minimal Apple-style indicators */}
            {((entry.people && entry.people.length > 0) || (entry.places && entry.places.length > 0)) && !entry.isAnalyzing && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {entry.people && entry.people.map((person, idx) => (
                  <span 
                    key={`person-${idx}`} 
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50/80 rounded-full text-[11px] font-medium text-blue-700/80"
                  >
                    <span className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center text-[10px]">
                      {person.name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                    {person.name}
                  </span>
                ))}
                {entry.places && entry.places.map((place, idx) => (
                  <span 
                    key={`place-${idx}`} 
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50/80 rounded-full text-[11px] font-medium text-amber-700/80"
                  >
                    <MapPin size={10} className="text-amber-500/70" />
                    {place.name}
                  </span>
                ))}
              </div>
            )}

            {/* Your Guide Section - Before Connections */}
            {!entry.isAnalyzing && (
            <div className="mt-4 pt-4 border-t border-black/[0.05]">
              {!adviceMap.has(entry.id) ? (
                <button
                  onClick={() => generateAdvice(entry)}
                  disabled={generatingAdviceForIds.has(entry.id)}
                  className="w-full p-3.5 bg-gradient-to-r from-purple-50 to-violet-50 hover:from-purple-100 hover:to-violet-100 border border-purple-100 rounded-xl transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-sm">
                      {generatingAdviceForIds.has(entry.id) ? (
                        <Loader2 size={16} className="text-white animate-spin" />
                      ) : (
                        <Compass size={16} className="text-white" />
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-[13px] font-bold text-purple-800">
                        {generatingAdviceForIds.has(entry.id) ? guideT.generating : guideT.discoverDirection}
                      </p>
                      <p className="text-[11px] text-purple-600/70 mt-0.5">
                        {guideT.discoverSubtitle}
                      </p>
                    </div>
                  </div>
                </button>
              ) : (
                <div className="space-y-3">
                  {/* Inspiring Insight - Always Visible */}
                  {(() => {
                    const advice = adviceMap.get(entry.id);
                    if (!advice?.oracleInsight) return null;
                    return (
                      <div className="p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl border border-purple-100/50">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-sm shrink-0">
                            <Sparkles size={14} className="text-white" />
                          </div>
                          <div className="flex-1">
                            <p className="text-[10px] font-bold text-purple-600 uppercase tracking-wide mb-1">
                              {guideT.title}
                            </p>
                            <p className="text-[14px] font-medium text-purple-900 leading-snug">
                              {advice.oracleInsight}
                            </p>
                          </div>
                        </div>
                        {/* See Full Plan CTA */}
                        <button
                          onClick={() => setExpandedAdviceId(expandedAdviceId === entry.id ? null : entry.id)}
                          className="w-full mt-3 flex items-center justify-center gap-2 py-2 bg-white/60 hover:bg-white rounded-lg transition-colors"
                        >
                          <span className="text-[12px] font-bold text-purple-700">
                            {expandedAdviceId === entry.id ? guideT.closePlan : guideT.viewFullPlan}
                          </span>
                          {expandedAdviceId === entry.id ? <ChevronUp size={14} className="text-purple-500" /> : <ChevronDown size={14} className="text-purple-500" />}
                        </button>
                      </div>
                    );
                  })()}
                  
                  {/* Full Plan - Expandable - Instagram Style */}
                  {expandedAdviceId === entry.id && (
                    <div className="space-y-3 animate-in slide-in-from-top-2 duration-200">
                      {(() => {
                        const advice = adviceMap.get(entry.id);
                        if (!advice) return null;
                        return (
                          <>
                            {/* Short Term - Amber/Orange Gradient */}
                            <div className="relative overflow-hidden rounded-2xl shadow-lg transition-all bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500">
                              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(255,255,255,0.15)_0%,transparent_50%)]" />
                              {advice.shortTermCompleted && <div className="absolute inset-0 bg-black/25" />}
                              <div className="relative p-4 flex items-start gap-3">
                                <button
                                  onClick={() => completeAction(entry.id, 'short', !advice.shortTermCompleted)}
                                  className={`w-8 h-8 rounded-full border-[2.5px] flex items-center justify-center shrink-0 transition-all active:scale-90 shadow-lg ${
                                    advice.shortTermCompleted 
                                      ? 'bg-emerald-500 border-emerald-400' 
                                      : 'bg-white/25 border-white/80 hover:bg-white/40'
                                  }`}
                                >
                                  {advice.shortTermCompleted ? (
                                    <Check size={18} className="text-white" strokeWidth={3} />
                                  ) : (
                                    <span className="w-3 h-3 rounded-full border-2 border-white/60" />
                                  )}
                                </button>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-lg drop-shadow-sm">💡</span>
                                    <p className="text-[11px] font-black text-white uppercase tracking-wider" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>{guideT.shortTerm}</p>
                                    {advice.shortTermCompleted && (
                                      <span className="px-2 py-0.5 bg-emerald-500 rounded-full text-[9px] font-bold text-white uppercase">✓</span>
                                    )}
                                  </div>
                                  <p className={`text-[14px] font-semibold leading-snug text-white ${advice.shortTermCompleted ? 'line-through opacity-70' : ''}`} 
                                     style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                                    {advice.shortTermAction}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Medium Term - Blue/Indigo Gradient */}
                            <div className="relative overflow-hidden rounded-2xl shadow-lg transition-all bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600">
                              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.12)_0%,transparent_45%)]" />
                              {advice.mediumTermCompleted && <div className="absolute inset-0 bg-black/25" />}
                              <div className="relative p-4 flex items-start gap-3">
                                <button
                                  onClick={() => completeAction(entry.id, 'medium', !advice.mediumTermCompleted)}
                                  className={`w-8 h-8 rounded-full border-[2.5px] flex items-center justify-center shrink-0 transition-all active:scale-90 shadow-lg ${
                                    advice.mediumTermCompleted 
                                      ? 'bg-emerald-500 border-emerald-400' 
                                      : 'bg-white/25 border-white/80 hover:bg-white/40'
                                  }`}
                                >
                                  {advice.mediumTermCompleted ? (
                                    <Check size={18} className="text-white" strokeWidth={3} />
                                  ) : (
                                    <span className="w-3 h-3 rounded-full border-2 border-white/60" />
                                  )}
                                </button>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-lg drop-shadow-sm">🎯</span>
                                    <p className="text-[11px] font-black text-white uppercase tracking-wider" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>{guideT.mediumTerm}</p>
                                    {advice.mediumTermCompleted && (
                                      <span className="px-2 py-0.5 bg-emerald-500 rounded-full text-[9px] font-bold text-white uppercase">✓</span>
                                    )}
                                  </div>
                                  <p className={`text-[14px] font-semibold leading-snug text-white ${advice.mediumTermCompleted ? 'line-through opacity-70' : ''}`}
                                     style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                                    {advice.mediumTermGoal}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Long Term - Violet/Purple Gradient */}
                            <div className="relative overflow-hidden rounded-2xl shadow-lg transition-all bg-gradient-to-br from-violet-500 via-purple-600 to-fuchsia-600">
                              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1)_0%,transparent_60%)]" />
                              {advice.longTermCompleted && <div className="absolute inset-0 bg-black/25" />}
                              <div className="relative p-4 flex items-start gap-3">
                                <button
                                  onClick={() => completeAction(entry.id, 'long', !advice.longTermCompleted)}
                                  className={`w-8 h-8 rounded-full border-[2.5px] flex items-center justify-center shrink-0 transition-all active:scale-90 shadow-lg ${
                                    advice.longTermCompleted 
                                      ? 'bg-emerald-500 border-emerald-400' 
                                      : 'bg-white/25 border-white/80 hover:bg-white/40'
                                  }`}
                                >
                                  {advice.longTermCompleted ? (
                                    <Check size={18} className="text-white" strokeWidth={3} />
                                  ) : (
                                    <span className="w-3 h-3 rounded-full border-2 border-white/60" />
                                  )}
                                </button>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-lg drop-shadow-sm">🚀</span>
                                    <p className="text-[11px] font-black text-white uppercase tracking-wider" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>{guideT.longTerm}</p>
                                    {advice.longTermCompleted && (
                                      <span className="px-2 py-0.5 bg-emerald-500 rounded-full text-[9px] font-bold text-white uppercase">✓</span>
                                    )}
                                  </div>
                                  <p className={`text-[14px] font-semibold leading-snug text-white ${advice.longTermCompleted ? 'line-through opacity-70' : ''}`}
                                     style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                                    {advice.longTermVision}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Connections Dropdown - After Guide */}
            {entry.relatedTo && entry.relatedTo.length > 0 && !entry.isAnalyzing && (
              <div className="mt-3">
                <button
                  onClick={() => setExpandedConnectionsId(expandedConnectionsId === entry.id ? null : entry.id)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-indigo-50/60 hover:bg-indigo-50 rounded-xl transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Link2 size={12} className="text-indigo-500" />
                    <span className="text-[11px] font-bold text-indigo-600">{entry.relatedTo.length} {t.journal.connections}</span>
                  </div>
                  {expandedConnectionsId === entry.id ? <ChevronUp size={14} className="text-indigo-400" /> : <ChevronDown size={14} className="text-indigo-400" />}
                </button>
                {expandedConnectionsId === entry.id && (
                  <div className="mt-2 space-y-2 animate-in slide-in-from-top-2 duration-200">
                    {entry.relatedTo.map((link, linkIdx) => {
                      const linkedEntry = entriesMap.get(link.targetEntryId);
                      if (!linkedEntry) return null;
                      return (
                        <div key={linkIdx} className="flex items-start gap-2 p-2.5 bg-indigo-50/50 rounded-lg border border-indigo-100/50">
                          <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-xs shrink-0">
                            {linkedEntry.emotion}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-medium text-black/80 leading-snug line-clamp-2">
                              {linkedEntry.content}
                            </p>
                            <div className="flex items-center gap-1.5 mt-1">
                              <span className="text-[8px] font-black uppercase text-indigo-600 px-1 py-0.5 bg-indigo-100 rounded">
                                {linkTypeLabels[link.linkType] || link.linkType}
                              </span>
                              <span className="text-[8px] text-black/30">
                                {new Date(linkedEntry.timestamp).toLocaleDateString(
                                  context.language === 'Italian' ? 'it-IT' :
                                  context.language === 'Spanish' ? 'es-ES' :
                                  context.language === 'German' ? 'de-DE' :
                                  context.language === 'French' ? 'fr-FR' : 'en-US',
                                  { day: 'numeric', month: 'short' }
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
          </div>
        )}
        </div>
      </div>

      {currentDiscoveryStep && (
        <FeatureDiscovery
          featureKey={currentDiscoveryStep}
          discoveryState={discoveryState}
          onDismiss={completeStep}
          onActivate={handleDiscoveryActivate}
          onNavigate={onNavigate}
          currentView="archive"
          language={context.language}
          position="bottom"
        />
      )}

      {/* AI Personality Selector Modal */}
      <PersonalitySelectorModal
        isOpen={isPersonalityModalOpen}
        onClose={() => setIsPersonalityModalOpen(false)}
        currentPersonalityKey={currentPersonalityKey}
        onSelect={onPersonalityChange!}
        language={context.language}
      />
    </div>
  );
};

export default JournalView;
